import { create } from 'zustand'
import axios from 'axios'
import { BACKEND } from '../utils/config'

export interface AdminUser {
  username:  string
  added_by:  string
  added_at:  string
}

interface AdminStore {
  isAdmin:   boolean
  checked:   boolean
  users:     AdminUser[]
  loading:   boolean
  error:     string | null

  checkAdmin:   (token: string) => Promise<boolean>
  fetchUsers:   (token: string) => Promise<void>
  addUser:      (token: string, username: string) => Promise<void>
  removeUser:   (token: string, username: string) => Promise<void>
  reset:        () => void
}

export const useAdminStore = create<AdminStore>((set, get) => ({
  isAdmin:  false,
  checked:  false,
  users:    [],
  loading:  false,
  error:    null,

  // ── Vérifie si l'utilisateur connecté est admin ─────────────────────────
  checkAdmin: async (token) => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/admin/me`, {
        headers: { 'x-mrc-auth': token },
      })
      set({ isAdmin: data.isAdmin, checked: true })
      return data.isAdmin
    } catch {
      set({ isAdmin: false, checked: true })
      return false
    }
  },

  // ── Liste tous les admins ───────────────────────────────────────────────
  fetchUsers: async (token) => {
    set({ loading: true, error: null })
    try {
      const { data } = await axios.get(`${BACKEND}/api/admin/users`, {
        headers: { 'x-mrc-auth': token },
      })
      set({ users: data.users || [], loading: false })
    } catch (e: any) {
      set({ error: e.response?.data?.error || e.message, loading: false })
    }
  },

  // ── Ajoute un admin ─────────────────────────────────────────────────────
  addUser: async (token, username) => {
    set({ loading: true, error: null })
    try {
      await axios.post(
        `${BACKEND}/api/admin/users`,
        { username },
        { headers: { 'x-mrc-auth': token } }
      )
      await get().fetchUsers(token)
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message
      set({ error: msg, loading: false })
      throw new Error(msg)
    }
  },

  // ── Supprime un admin ───────────────────────────────────────────────────
  removeUser: async (token, username) => {
    set({ loading: true, error: null })
    try {
      await axios.delete(`${BACKEND}/api/admin/users/${username}`, {
        headers: { 'x-mrc-auth': token },
      })
      await get().fetchUsers(token)
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message
      set({ error: msg, loading: false })
      throw new Error(msg)
    }
  },

  reset: () => set({ isAdmin: false, checked: false, users: [], error: null }),
}))